import { PropType, computed, defineComponent } from "vue";
import useVuelidate from "@vuelidate/core";
import { Container } from "aurelia-dependency-injection";
import { required, requiredIf } from "@vuelidate/validators";
import moment from "moment";

import { VisitDetails } from "../../../vue-models/visit-details";
import { ScheduleVisitHomeHealthModel } from "../../../vue-models/schedule-visit-home-health";
import { IEmployeeList, IReassignVisitDetails } from "../../../vue-interfaces/i-reassign-visit";
import { ITypeaheadOptions } from "../../../vue-interfaces/i-typeahead";
import { INewVisitDetailsResponse, ITaskDetailsPayload } from "../../../vue-interfaces/i-task-details";
import { IDisciplineTaskHH, IGetDisciplineTaskHHParams } from "../../../vue-interfaces/i-discipline-task-hh";
import { IGetPatientByBranchResponse, IPatientByAgencyRequest } from "../../../vue-interfaces/i-patient-service";
import { IAvailableTaskItems } from "../../../vue-interfaces/i-patient-task-hos-pal";
import {
    ICurrentEpisodeRequestParams,
    ICurrentEpisodeResponse,
    PatientService
} from "../../../../services/patient.service";
import {
    ICreateNonPatientActivityHH,
    ICreateNonPatientActivityHHHCResponse,
    ICreateNonPatientActivityHHRequest,
    IFrequencyListRequestParams,
    IFrequencyListResponse,
    ITaskByPatientRequest,
    ITaskByPatientResponse,
    TaskService
} from "../../../../services/task.service";
import { ToastrService } from "../../../../services/toastr.service";
import { LookupService } from "../../../../services/lookup-service";
import { BranchesService } from "../../../../services/branches-service";

import FormSection from "../../../vue-custom-elements/FormSection/FormSection.vue";
import TypeaheadInput from "../../../vue-custom-elements/TypeaheadInput/TypeaheadInput.vue";
import MultiDatePickerHH from "../../../vue-custom-elements/MultiDatePickerHH/MultiDatePickerHH.vue";
import DatePicker from "../../../vue-custom-elements/DatePicker/DatePicker.vue";
import { formatDateInput } from "../../../../common/vue-helpers/modifiers/value-modifier";
import TimePicker from "../../../vue-custom-elements/TimePicker/TimePicker.vue";
import CommentInput from "../../../vue-custom-elements/CommentInput/CommentInput.vue";
import SaveNotifyUser from "../../../vue-custom-elements/SaveNotifyUser/SaveNotifyUser.vue";

export enum SaveEnum {
    SaveAndExit = "1",
    SaveAndAddAnother = "2",
    SaveAndNotifyEmployee = "3"
}

export interface IVisitOrders {
    discipline: string;
    frequency: string;
    effectiveDate: string;
}

export default defineComponent({
    components: {
        FormSection,
        TypeaheadInput,
        MultiDatePickerHH,
        DatePicker,
        TimePicker,
        CommentInput,
        SaveNotifyUser
    },

    provide() {
        return {
            dialogRef: computed(() => this.dialogRef)
        };
    },

    props: {
        visitDetails: { type: Object as PropType<IReassignVisitDetails>, default: {} },
        isLoading: { type: Boolean, default: false },
        isSaving: { type: Boolean, default: false },
        cancel: { type: Function, default: null },
        ok: { type: Function, default: null },
        addAnother: { type: Function, default: null }
    },

    data() {
        return {
            employeeList: [] as IEmployeeList[],
            showAllByDefault: true as boolean,
            _taskService: null,
            _toastrService: null,
            _lookupService: null,
            _patientService: null,
            _branchesService: null,
            commentInputClass: { input: "w-35" } as { input: string; textarea?: string },
            taskDetails: {} as VisitDetails,
            isSavingReason: false as boolean,
            showAssignedToList: false as boolean,
            assignedToName: "" as string,
            newVisitDetails: new ScheduleVisitHomeHealthModel(),
            disciplineTasks: [] as IDisciplineTaskHH[],
            disciplineTask: null as IDisciplineTaskHH,
            selectedTask: {} as ITypeaheadOptions,
            disciplineTasksList: [] as Array<ITypeaheadOptions>,
            patients: [] as IGetPatientByBranchResponse[],
            currentUserId: "" as string,
            currentEpisodeDetails: null as ICurrentEpisodeResponse,
            patientFrequencyList: [] as IFrequencyListResponse[],
            visitOrders: [] as IVisitOrders[],
            visitDates: "" as string,
            tasks: [] as ITaskByPatientResponse[],
            visitDatesList: [],
            patientId: null as ITypeaheadOptions,
            dialogRef: null,
            SaveEnum,
            v$: useVuelidate(),
            isNva: false,
            newTaskSingleDate: "",
            formatDateInput,
            selectedBranch: null,
            selectedBranches: [] as any,
            allBranchList: [],
            nvaActivity: null,
            DestinationList: [] as any,
            Destination: 0 as number,
            comments: "",
            nvaTaskType: [] as IAvailableTaskItems[],
            associatedMileage: 0
        };
    },

    validations() {
        return {
            selectedTask: { required: requiredIf(!this.isNva) },
            newVisitDetails: {
                patientId: { required: requiredIf(!this.isNva) }
            },
            visitDates: {
                required: requiredIf(!this.isNva)
            },
            newTaskSingleDate: {
                required: requiredIf(this.isNva)
            },
            selectedBranch: {
                id: { required: requiredIf(this.isNva) }
            },
            nvaActivity: { required: requiredIf(this.isNva) }
        };
    },

    watch: {
        visitDateChanged() {
            this.visitDatesChanged();
        },
        patientId(data) {
            this.getPatientTasks(data.value);
        },
        selectedTask(newValue: ITypeaheadOptions) {
            if (!!newValue) {
                const taskDetails = this.disciplineTasks.find((task) => {
                    return task.taskName == newValue.name;
                });
                if (taskDetails) {
                    this.disciplineTask = taskDetails;
                    this.newVisitDetails.disciplineTask = this.disciplineTask.disciplineTask;
                    this.newVisitDetails.customId = this.disciplineTask.customId;
                }
            }
        },
        async isNva(data) {
            await this.getNvaTasks();
        }
    },

    emits: ["loadingChange", "savingChange"],

    created() {
        this._taskService = Container.instance.get(TaskService);
        this._toastrService = Container.instance.get(ToastrService);
        this._lookupService = Container.instance.get(LookupService);
        this._patientService = Container.instance.get(PatientService);
        this._branchesService = Container.instance.get(BranchesService);
        this.DestinationList = ["NA", "Branch Office", "Facility", "Patient Home", "Physician Office", "Other"];
    },

    async mounted() {
        Object.assign(this.visitDetails, {});
        try {
            this.$emit("loadingChange", true);
            const payload: ITaskDetailsPayload = {
                application: this.visitDetails.Application,
                agencyId: this.visitDetails.AgencyId,
                eventId: this.visitDetails.Id,
                episodeId: this.visitDetails.EpisodeId
            };
            const visitDetails = await this._taskService.getHHVisitDetails(payload);
            this.newVisitDetails.agencyId = visitDetails.AgencyId;
            this.newVisitDetails.application = visitDetails.Application;
            this.newVisitDetails.disciplineTask = null;
            await this.getDisciplineTasks();
            await this.getPatient("");
            this.allBranchList = await this._branchesService.getAllBranches();
            this.selectedBranches = this.allBranchList.filter(
                (branch) => branch.accountId == this.visitDetails.AgencyId
            );
            this.selectedBranch = this.selectedBranches?.filter((branch) => branch.isPatientIdentifierEnabled)?.pop();
            await this.getNvaTasks();
        } catch (error) {
            console.log(error);
        } finally {
            this.$emit("loadingChange", false);
        }
    },

    methods: {
        formatTime(dateTime: Date) {
            return dateTime
                ? new Date(dateTime).toLocaleTimeString("en-us", {
                      hour: "2-digit",
                      minute: "2-digit"
                  })
                : "";
        },

        patientIdChanged(newValue: ITypeaheadOptions) {
            if (newValue) {
                this.patientId = newValue;
                this.newVisitDetails.patientId = newValue.value;
                this.visitOrders = [];
                this.currentEpisodeDetails = null;
                this.visitDates = "";
                this.getCurrentEpisode();
            }
        },

        availableTasksFilter(filter: string) {
            if (filter) {
                const filterAvailableTasks = this.disciplineTasksList.filter((task) => {
                    return task.name.toLowerCase().includes(filter.toLowerCase());
                });
                return filterAvailableTasks;
            } else {
                return this.disciplineTasksList;
            }
        },

        changeTaskList(data: any) {
            this.selectedTask = data;
        },

        updateResult(result: any) {
            this.visitDates = result;
        },

        async getDisciplineTasks() {
            try {
                const payload: IGetDisciplineTaskHHParams = {
                    agencyId: this.visitDetails.AgencyId,
                    application: this.visitDetails.Application
                };
                this.disciplineTasks = await this._lookupService.getDisciplineTasks(payload);
                this.disciplineTasksList = this.disciplineTasks.map((item) => {
                    return {
                        name: item.taskName,
                        value: item.discipline,
                        disciplineTask: item.disciplineTask.toString(),
                        customId: item.customId.toString()
                    };
                });
            } catch (e) {
                console.log(e);
                this._toastrService.error({
                    title: `Fetching Visit Type Failed`,
                    message: `Fetching Visit Type Operation Failed, Please Contact Axxess if issue still persists.`
                });
            }
        },

        async getCurrentEpisode() {
            try {
                const payload: ICurrentEpisodeRequestParams = {
                    agencyId: this.visitDetails.AgencyId,
                    application: this.visitDetails.Application,
                    patientId: this.newVisitDetails.patientId
                };
                this.currentEpisodeDetails = await this._patientService.getCurrentEpisode(payload);
                this.newVisitDetails.episodeId = this.currentEpisodeDetails.id;
                this.getPatientFrequencyList();
            } catch (e) {
                console.log(e);
                this._toastrService.error({
                    title: `Fetching Current Episode Failed`,
                    message: `Fetching Current Episode Operation Failed, Please Contact Axxess if issue still persists.`
                });
            }
        },

        async getPatientFrequencyList() {
            try {
                const payload: IFrequencyListRequestParams = {
                    agencyId: this.visitDetails.AgencyId,
                    application: this.visitDetails.Application,
                    patientId: this.newVisitDetails.patientId,
                    episodeId: this.currentEpisodeDetails?.id,
                    episodeStartDate: this.currentEpisodeDetails?.startDate
                };
                this.patientFrequencyList = await this._taskService.getFrequencyList(payload);

                this.patientFrequencyList.map((patientFrequency) => {
                    let visitOrder: IVisitOrders = null;
                    if (patientFrequency.frequencyHHA) {
                        visitOrder = {
                            discipline: "HHA",
                            frequency: patientFrequency.frequencyHHA,
                            effectiveDate: moment(patientFrequency.startDate).format("MM/DD/YYYY")
                        };
                        this.visitOrders.push(visitOrder);
                    }
                    if (patientFrequency.frequencyMSW) {
                        visitOrder = {
                            discipline: "MSW",
                            frequency: patientFrequency.frequencyMSW,
                            effectiveDate: moment(patientFrequency.startDate).format("MM/DD/YYYY")
                        };
                        this.visitOrders.push(visitOrder);
                    }
                    if (patientFrequency.frequencyNursing) {
                        visitOrder = {
                            discipline: "SN",
                            frequency: patientFrequency.frequencyNursing,
                            effectiveDate: moment(patientFrequency.startDate).format("MM/DD/YYYY")
                        };
                        this.visitOrders.push(visitOrder);
                    }
                    if (patientFrequency.frequencyOT) {
                        visitOrder = {
                            discipline: "OT",
                            frequency: patientFrequency.frequencyOT,
                            effectiveDate: moment(patientFrequency.startDate).format("MM/DD/YYYY")
                        };
                        this.visitOrders.push(visitOrder);
                    }
                    if (patientFrequency.frequencyPT) {
                        visitOrder = {
                            discipline: "PT",
                            frequency: patientFrequency.frequencyPT,
                            effectiveDate: moment(patientFrequency.startDate).format("MM/DD/YYYY")
                        };
                        this.visitOrders.push(visitOrder);
                    }
                    if (patientFrequency.frequencyST) {
                        visitOrder = {
                            discipline: "ST",
                            frequency: patientFrequency.frequencyST,
                            effectiveDate: moment(patientFrequency.startDate).format("MM/DD/YYYY")
                        };
                        this.visitOrders.push(visitOrder);
                    }

                    this.visitOrders = this.visitOrders.reduce((acc, curr) => {
                        const existingEntry = acc.find((item) => item.discipline === curr.discipline);
                        if (existingEntry) {
                            existingEntry.frequency += ` \n${curr.frequency}`;
                            existingEntry.effectiveDate += ` \n${curr.effectiveDate}`;
                        } else {
                            acc.push(curr);
                        }
                        return acc;
                    }, []);

                    return this.visitOrders;
                });
            } catch (e) {
                console.log(e);
                this._toastrService.error({
                    title: `Fetching Frequency List Failed`,
                    message: `Fetching Frequency List Operation Failed, Please Contact Axxess if issue still persists.`
                });
            }
        },

        async getPatientTasks(patientId: string) {
            this.$emit("loadingChange", true);
            try {
                const startDate = moment().startOf("month").format("YYYY-MM-DD");
                const endDate = moment(startDate).add(2, "months").endOf("month").format("YYYY-MM-DD");

                const payload: ITaskByPatientRequest = {
                    patientIds: [patientId],
                    start: startDate,
                    end: endDate
                };
                this.tasks = await this._taskService.getTasksByPatients(payload);
                this.visitDatesList = this.tasks.map((item: ITaskByPatientResponse) => ({
                    ...item,
                    StartDate: new Date(item.StartDate)
                }));
            } catch (e) {
                console.error(e);
            } finally {
                this.$emit("loadingChange", false);
            }
        },

        async getPatient(term: string) {
            const patientPayload: IPatientByAgencyRequest = {
                agencyId: this.visitDetails.AgencyId,
                application: this.visitDetails.Application,
                searchTerm: term,
                page: 1,
                pageLength: 20
            };
            this.patients = await this._patientService.getPatientsByAgency(patientPayload);

            return this.patients?.map((item) => {
                return {
                    name: `${item.PatientName.FirstName} ${item.PatientName.LastName}`,
                    value: item.Id
                };
            });
        },

        async scheduleVisit(saveType: string) {
            const valid = await this.v$.$validate();
            if (!valid) {
                return;
            }
            if (saveType == SaveEnum.SaveAndNotifyEmployee) {
                this.newVisitDetails.NotifyUser = true;
                this.newVisitDetails.PatientName = this.patientId.name;
                this.newVisitDetails.TaskName = this.selectedTask.name;
            }
            if (!!this.newVisitDetails) {
                try {
                    this.visitDatesChanged();
                    this.$emit("savingChange", true);
                    if (!this.isNva) {
                        this.newVisitDetails.userId = this.visitDetails.UserId;
                        this.newVisitDetails.agencyId = this.visitDetails.AgencyId;
                        this.newVisitDetails.application = this.visitDetails.Application;
                        const selectedTask = this.disciplineTasks.find(
                            (task: any) =>
                                task.customId === this.selectedTask.customId &&
                                task.disciplineTask === this.selectedTask.disciplineTask
                        );
                        if (selectedTask) {
                            this.newVisitDetails.disciplineTask = selectedTask.disciplineTask;
                            this.newVisitDetails.customId = selectedTask.customId;
                            this.newVisitDetails.version = selectedTask.version;
                        }
                        const newVisitDetailsResp: INewVisitDetailsResponse = await this._taskService.scheduleVisitHH(
                            this.newVisitDetails
                        );
                        const isWarning = newVisitDetailsResp.Message.includes("Warning");
                        const respMessage = {
                            title: `New Task Created`,
                            message: `The task has been created`
                        };
                        if (isWarning) {
                            this._toastrService.warning(respMessage);
                        } else {
                            this._toastrService.success(respMessage);
                        }
                    } else {
                        const hhRequest = {} as ICreateNonPatientActivityHHRequest;
                        hhRequest.AgencyId = this.visitDetails.AgencyId;
                        hhRequest.RequestPayload = [] as ICreateNonPatientActivityHH[];
                        let nonVisitDetail = {} as ICreateNonPatientActivityHH;
                        let nvaDate = moment(this.newTaskSingleDate).format("YYYY-MM-DD");
                        nonVisitDetail.AgencyId = this.visitDetails.AgencyId;
                        nonVisitDetail.LocationId = this.selectedBranch.id;
                        nonVisitDetail.TaskId = this.nvaActivity.id;
                        nonVisitDetail.UserId = this.visitDetails.UserId;
                        nonVisitDetail.Destination = this.Destination;
                        nonVisitDetail.TaskDate = this.newTaskSingleDate;
                        nonVisitDetail.TimeIn = this.visitDetails.TimeIn;
                        nonVisitDetail.TimeOut = this.visitDetails.TimeOut;
                        nonVisitDetail.TravelStartTime = this.visitDetails.StartTime;
                        nonVisitDetail.TravelEndTime = this.visitDetails.EndTime;
                        nonVisitDetail.AssociatedMileage = this.associatedMileage;
                        nonVisitDetail.Comments = this.comments;
                        if (saveType == SaveEnum.SaveAndNotifyEmployee) {
                            nonVisitDetail.NotifyUser = true;
                            nonVisitDetail.TaskName = this.selectedTask.name;
                        }
                        hhRequest.RequestPayload.push(nonVisitDetail);
                        const newVisitDetailsResp: ICreateNonPatientActivityHHHCResponse =
                            await this._taskService.CreateHHNvaTask(hhRequest);
                        const respMessage = {
                            title: `New Non-Visit Task Created.`,
                            message: `The task has been created`
                        };

                        if (!newVisitDetailsResp.IsSuccessful) {
                            respMessage.message = newVisitDetailsResp.ErrorMessage ?? "unable to create NVA task";
                            respMessage.title = `Nva task error message`;
                            this._toastrService.error(respMessage);
                        } else {
                            this._toastrService.success(respMessage);
                        }
                    }

                    if (saveType == SaveEnum.SaveAndExit || saveType == SaveEnum.SaveAndNotifyEmployee) {
                        this.ok();
                    } else if (saveType == SaveEnum.SaveAndAddAnother) {
                        this.addAnother();
                    } else {
                        this.newVisitDetails.disciplineTask = null;
                        this.newVisitDetails.visitDates = [];
                        this.newVisitDetails.patientId = null;
                        this.visitDates = "";
                        this.patientId = null;
                        this.disciplineTask = null;
                    }
                } catch (e) {
                    console.log(JSON.stringify(e));
                    this._toastrService.error({
                        title: `Visit Scheduled Failed`,
                        message: `Scheduling Visit Operation Failed, Please Contact Axxess if issue still persists.`
                    });
                } finally {
                    this.$emit("savingChange", false);
                }
            }
        },

        visitDatesChanged() {
            if (this.visitDates) {
                this.newVisitDetails.visitDates = this.visitDates.split(", ");
            } else {
                this.newVisitDetails.visitDates = [];
            }
        },

        onDateChange(date: any, name: string) {
            if (name === "new-task-single-date") this.newTaskSingleDate = date;
        },

        nvaAvailableTasksFilter(filter: string) {
            if (filter) {
                const filterAvailableTasks = this.nvaTaskType.filter((task) => {
                    return task?.name.toLowerCase().includes(filter.toLowerCase());
                });
                return filterAvailableTasks;
            } else {
                return this.nvaTaskType;
            }
        },

        nvaActivityChanged(newValue: object) {
            this.nvaActivity = newValue;
        },

        async getNvaTasks() {
            if (this.isNva) {
                const data = await this._taskService.getNvaTaskTypes({
                    AgencyId: this.visitDetails.AgencyId,
                    Application: this.visitDetails.Application
                });
                this.nvaTaskType = data.map((val) => {
                    return { id: val.Id, name: val.Text };
                });
                return true;
            } else {
                return false;
            }
        },

        changeTravelStartTime(time: any) {
            this.visitDetails.StartTime = time;
        },

        changeTravelEndTime(time: any) {
            this.visitDetails.EndTime = time;
        },

        changeVisitTimeIn(time: any) {
            this.visitDetails.TimeIn = time;
        },

        changeVisitTimeOut(time: any) {
            this.visitDetails.TimeOut = time;
        }
    }
});
